import { Star, Figma, Clock, icons } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import Link from "next/link";
import { Icon } from "@/components/ui/icon";

interface TrainingCardProps {
  slug?: string;
  icon?: string;
  title: string;
  subtitle: string;
  duration: string;
  hours: string;
  rating: number;
  markAsNew?: boolean;
  audience: { title: string; description: string }[];
  features: string[];
  className?: string;
  remainingPlaces: number;
}

export function TrainingCard({
  slug,
  icon,
  title,
  subtitle,
  duration,
  hours,
  rating,
  markAsNew,
  audience,
  features,
  className,
  remainingPlaces,
}: TrainingCardProps) {
  return (
    <Card className={`flex flex-col ${className} bg-card text-card-foreground`}>
      <CardHeader className="space-y-3 pb-4">
        <div className="flex items-center justify-between">
          <div className="rounded-full bg-secondary p-2">
            {icon && (
              <Icon
                name={icon as keyof typeof icons}
                className="h-6 w-6 text-secondary-foreground/80"
              />
            )}
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex items-center">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span className="ml-1 text-sm font-medium">
                {Number.isInteger(rating) ? rating : rating.toFixed(1)}/5
              </span>
            </div>
            {markAsNew ? (
              <Badge
                variant="secondary"
                className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 pointer-events-none"
              >
                Nouveau
              </Badge>
            ) : (
              <Badge
                variant="secondary"
                className="bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-100 pointer-events-none"
              >
                {`-${remainingPlaces} places`}
              </Badge>
            )}
          </div>
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold leading-none tracking-tight">
            {title}
          </h3>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span className="font-medium">{duration}</span>
          <span>•</span>
          <span>{hours}</span>
        </div>
      </CardHeader>
      <CardContent className="grid gap-6 px-6">
        <div className="flex flex-wrap gap-2">
          {audience.map((item, index) => (
            <Badge
              key={index}
              variant="secondary"
              className="bg-muted dark:bg-input text-muted-foreground pointer-events-none"
            >
              {item.title}
            </Badge>
          ))}
        </div>
        <ul className="space-y-2 text-sm">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <span className="mr-2 text-secondary-foreground">•</span>
              {feature}
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter className="mt-auto pt-6 px-6">
        <Button
          variant="outline"
          // className="w-full text-secondary-foreground hover:bg-secondary/80"
          className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/80"
          asChild
        >
          <Link href={`/academy/${slug}`}>
            En savoir plus
            <svg
              className="ml-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M9 5l7 7-7 7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
